<template>
  <!-- The site's one button. Every variant is a solid shape with a one-step-
       lighter inset ring, and hover fills to the ring's colour — the rule the
       generator's "Generate Palette" and the API ad's call to action set.

       `primary`   indigo: the single most important action on a screen.
       `secondary` gray: the action attached to a field (Generate, Extract,
                   Copy), where indigo would compete with the page's CTA.
       `soft`      translucent: low-emphasis actions in groups — footer pills,
                   share buttons, "Yes / No".

       Renders a <button> by default; with `href` it becomes an external <a>
       (new tab) or a <NuxtLink> for an in-app route. Other attributes (id,
       aria-*, listeners) fall through to that element. Focus uses the global
       ring from assets/css/main.css.
       Slots: `icon` (leading), default (label), `iconAfter` (trailing). -->
  <component
    :is="tag"
    v-bind="linkAttrs"
    class="inline-flex items-center justify-center rounded-md font-semibold ring-1 ring-inset transition disabled:cursor-not-allowed disabled:opacity-50"
    :class="[VARIANTS[variant], SIZES[size]]"
  >
    <slot name="icon" />
    <slot />
    <slot name="iconAfter" />
  </component>
</template>

<script setup>
import { usePolymorphicLink } from '~/composables/usePolymorphicLink'

const props = defineProps({
  href: { type: String, default: '' },
  variant: { type: String, default: 'primary' },
  // xs: inline pills · sm: compact panels · md: default · lg: a page's hero CTA
  size: { type: String, default: 'md' },
  type: { type: String, default: 'button' },
})

const VARIANTS = {
  primary: 'bg-indigo-500 text-white shadow-sm ring-indigo-400 hover:bg-indigo-400',
  secondary: 'bg-gray-600 text-white shadow-sm ring-gray-500 hover:bg-gray-500',
  soft: 'bg-white/10 text-white ring-white/10 hover:bg-white/20',
}

const SIZES = {
  xs: 'gap-1.5 px-2 py-1 text-xs',
  sm: 'gap-1.5 px-3 py-1.5 text-sm',
  md: 'gap-2 px-3.5 py-2.5 text-sm',
  lg: 'gap-2 px-6 py-3.5 text-base',
}

const { tag, attrs: linkAttrs } = usePolymorphicLink(() => props.href, () => props.type)
</script>
